import Component from '@glimmer/component'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type { BufferedChangeset } from 'ember-changeset/types'
import type { BaseOption, ProgressHandler, ProgressionRecord } from 'teacher-ui-client/progress-handler/utils'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { EditProgressOptions } from 'teacher-ui-client/components/form/edit-progress/options'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { applyAttr } from 'teacher-ui-client/modifiers/apply-attr'

interface Args {
  /**
   * The handler for this progress type. Provides the data and functions needed to determine the available progress
   * options, etc
   */
  progressHandler: ProgressHandler

  /**
   * A changeset to save the progress selection to.
   */
  progressChangeset: BufferedChangeset
  /**
   * An array of progression items from student-progress service for a student. Used to determine initial selected
   * option. An empty array will mean no option is selected initially.
   */
  studentProgression?: ProgressionRecord[]

  themeKey?: ThemeKeys
}

interface Signature {
  Element: HTMLSelectElement
  Args: Args
}

export class EditProgressSelect extends Component<Signature> {
  get studentProgression() {
    return this.args.studentProgression ?? []
  }

  /**
   * Returns the selected option for the handler option type.
   * This will always return the option selected by the user if they have made a choice. If they are yet to choose, it
   * will use the given progression data (for a single student) to prefill the students' current progress value, or
   * otherwise no option selected (aka "No Change")
   */
  get selectedOption(): BaseOption | undefined {
    const { progressHandler, progressChangeset } = this.args

    const selectedOption = progressChangeset.get(progressHandler.progressType) as BaseOption | undefined
    const initialOption = progressHandler.getSavedOption(this.studentProgression)

    return selectedOption ?? initialOption
  }

  /**
   * Selects an individual progress option. Should be given the unique key of the changeset item to update, with the
   * option item that was chosen.
   *
   * Will rollback if optionId undefined or optionId is same as initial option.
   */
  @action
  onSelectProgressOption(handler: ProgressHandler, optionId?: BaseOption['optionId']) {
    const changesetKey = handler.progressType
    const allOptions = [...handler.lessonOptions, handler?.placementOption]
    const selectedOption = allOptions.find((opt) => opt?.optionId === optionId)

    const initialOption = handler.getSavedOption(this.studentProgression)

    if (selectedOption && selectedOption.optionId !== initialOption?.optionId) {
      this.args.progressChangeset.set(changesetKey, selectedOption)
    } else {
      this.args.progressChangeset.rollbackProperty(changesetKey)
    }
  }

  <template>
    <BaseSelect
      data-test-edit-progress-select={{@progressHandler.progressType}}
      id={{@progressHandler.progressType}}
      name={{@progressHandler.progressType}}
      value={{this.selectedOption.optionId}}
      @themeKey={{@themeKey}}
      {{on "input" (eventValue (fn this.onSelectProgressOption @progressHandler))}}
      {{applyAttr value=this.selectedOption.optionId}}
      ...attributes
    >
      <EditProgressOptions
        @selectedOption={{this.selectedOption}}
        @flatOptions={{@progressHandler.flatOptions}}
        @groupedOptions={{@progressHandler.groupedOptions}}
      />
    </BaseSelect>
  </template>
}

export default EditProgressSelect
